import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Logo from '~/components/shared/Logo/Logo';
import {
  FontWeights,
  Typography,
} from '~/components/shared/Typography/Typography';
import { MediaQueries } from '~/constants/constVars';
import useTranslate from '~/hooks/useTranslate';
import { setLanguage } from '~/store/slices/intl.slice';
import { Language, RootState } from '~/store/types/sharedTypes';
import { ComponentSize } from '~/styles/BaseStyledComponents';
import { theme } from '~/styles/themes';

const InvoiceHeaderContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-around;
  background-color: ${theme.colors.white};
  box-shadow: 0 0.25em 1.25em -0.25em #00000020;
  margin-bottom: 2.5em;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  font-size: 20px;
`;

const InvoiceHeaderChildrenContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-right: 2em;
  padding-left: 2em;
  @media (orientation: landscape) {
    max-width: 90%;
  }
`;

const InvoiceHeaderBackButtonHolder = styled.div`
  flex: 0.3;
  display: flex;
  align-items: center;
  justify-content: space-around;
  cursor: pointer;
  white-space: nowrap;
`;

const InvoiceHeaderLogoHolder = styled.div`
  flex: 0.25;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const InvoiceHeaderInvisibleHolder = styled.div`
  flex: 1;
`;

const LanguagePicker = styled.div`
  flex: 1;
  float: right;
`;

const Option = styled.span<{ active?: boolean }>`
  padding-left: 1em;
  cursor: pointer;
  &:not(:first-of-type) {
    border-left: 1px solid ${theme.colors.neutral};
  }
  &:hover {
    color: ${theme.colors.hoverGreen};
  }
  color: ${(props) =>
    props.active ? theme.colors.active : theme.colors.neutral};
  text-decoration: ${(props) => (props.active ? 'underline' : 'none')};
  @media ${MediaQueries.smallScreen} {
    font-size: 1em;
  }
`;

export const LanguageContainer = styled.div`
  display: flex;
  @media (max-width: 850px) {
    margin-top: 1em;
  }
`;

interface Props {
  backButtonLabel: string;
  onBackButton: () => void;
  languagePicker?: boolean;
  customerService?: boolean;
  onCustomerService?: () => void;
  highlightedSection?: string;
}

const InvoicePageHeader: React.FC<Props> = (props) => {
  const {
    onBackButton,
    backButtonLabel,
    languagePicker,
    customerService,
    onCustomerService,
    highlightedSection,
  } = props;

  const { intl } = useSelector((state: RootState) => state);
  const { language } = intl;

  const translate = useTranslate();
  const dispatch = useDispatch();

  const handleLanguageChanged = (newLanguage: Language) => {
    dispatch(setLanguage(newLanguage));
    localStorage.setItem('qredLoginLanguage', newLanguage);
  };

  return (
    <InvoiceHeaderContainer>
      <InvoiceHeaderChildrenContainer>
        <InvoiceHeaderBackButtonHolder onClick={onBackButton}>
          <InvoiceHeaderLogoHolder>
            <Logo
              mt={1}
              mb={1}
              size={ComponentSize.Small}
              style={{ height: '1.5em' }}
            />
          </InvoiceHeaderLogoHolder>
          <Typography
            color={theme.colors.accent}
            fontSize={1}
            fontWeight={FontWeights.Thin}
            style={
              highlightedSection === 'myInvoices' ? { fontWeight: 700 } : {}
            }
          >
            {translate(backButtonLabel)}
          </Typography>
        </InvoiceHeaderBackButtonHolder>
        {customerService ? (
          <Typography
            color={theme.colors.accent}
            fontSize={1}
            fontWeight={FontWeights.Thin}
            onClick={onCustomerService}
            style={
              highlightedSection === 'customerSupport'
                ? { cursor: 'pointer', fontWeight: 700 }
                : { cursor: 'pointer' }
            }
          >
            {translate('Invoices.CustomerService')}
          </Typography>
        ) : (
          <InvoiceHeaderInvisibleHolder style={{ flex: 0.5 }} />
        )}
        {(languagePicker === false || languagePicker === undefined) && (
          <InvoiceHeaderInvisibleHolder />
        )}
        {languagePicker && (
          <LanguagePicker>
            <LanguageContainer style={{ float: 'right', margin: '0px' }}>
              <Option
                style={{ paddingRight: '1em' }}
                key={Language.English}
                active={language === Language.English}
                onClick={() => handleLanguageChanged(Language.English)}
              >
                EN
              </Option>
              <Option
                key={Language.Swedish}
                active={language === Language.Swedish}
                onClick={() => handleLanguageChanged(Language.Swedish)}
              >
                SV
              </Option>
            </LanguageContainer>
          </LanguagePicker>
        )}
      </InvoiceHeaderChildrenContainer>
    </InvoiceHeaderContainer>
  );
};

export default InvoicePageHeader;
